// Shared WWOOF card theme helpers.
// Site-specific xprofile field mapping used by both member cards and map popups.

export const WWOOF_STATUS_FIELD = '144';

export const WWOOF_HOST_META_FIELDS = [
    { id: '21', icon: 'people', prefixes: ['Qty:'] },
    { id: '28', icon: 'calendar', prefixes: ['Stay:'] },
    { id: '124', icon: 'pin', prefixes: ['State:'] }
];

export const WWOOF_HOST_FIELD_IDS = new Set([WWOOF_STATUS_FIELD, '21', '28', '124']);

export const ICON_SVG_PATHS = {
    people: '<circle cx="9" cy="8" r="3"></circle><path d="M3.5 20v-1.2A4.8 4.8 0 0 1 8.3 14h1.4a4.8 4.8 0 0 1 4.8 4.8V20"></path><path d="M16 5.5a3 3 0 0 1 0 5.8"></path><path d="M17 14.2a4.8 4.8 0 0 1 4 4.8V20"></path>',
    calendar: '<rect x="3" y="4.5" width="18" height="17" rx="2"></rect><path d="M16 2.5v4M8 2.5v4M3 9.5h18"></path><path d="M8 13h.01M12 13h.01M16 13h.01M8 17h.01M12 17h.01"></path>',
    pin: '<path d="M20 10.5c0 5.3-8 11-8 11s-8-5.7-8-11a8 8 0 1 1 16 0Z"></path><circle cx="12" cy="10.5" r="2.7"></circle>',
    user: '<circle cx="12" cy="8" r="3.4"></circle><path d="M5.5 20a6.5 6.5 0 0 1 13 0"></path>',
    venus: '<circle cx="12" cy="8.5" r="4.5"></circle><path d="M12 13v8M9 18h6M15.2 5.3 18 2.5"></path>',
    mars: '<circle cx="10" cy="14" r="4.5"></circle><path d="m13.2 10.8 7-7M15.5 4H20v4.5"></path>'
};

// HTML string version of the SVG helper for Leaflet popup markup.
export const iconSvgHtml = (type) =>
    `<svg class="wwoof-meta-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICON_SVG_PATHS[type] || ''}</svg>`;

export const cleanWwoofText = (value, prefixes) => {
    let text = (value || '').replace(/\s+/g, ' ').trim();
    (prefixes || []).forEach((prefix) => {
        const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        text = text.replace(new RegExp('^' + escaped + '\\s*', 'i'), '');
    });
    return text.trim();
};

const decodeHtmlEntities = (str) => {
    if (!str) return str;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = str;
    return textarea.value;
};

export const fieldText = (field) => {
    if (!field || field.type === 'custom_format') return '';
    const value = field.value;
    if (value === null || value === undefined) return '';
    const decoded = Array.isArray(value)
        ? value.map((v) => decodeHtmlEntities(String(v))).join(', ')
        : decodeHtmlEntities(String(value));
    return cleanWwoofText(decoded, []);
};

export const isWwoofField = (field) => {
    if (WWOOF_HOST_FIELD_IDS.has(String(field.field_id))) return true;
    const label = decodeHtmlEntities(field.label || '').toLowerCase();
    return label.indexOf('age group') !== -1 || label.indexOf('gender') !== -1;
};

export const getWwoofStatusClass = (text) => {
    const value = (text || '').toLowerCase();
    if (value.indexOf('enquire') !== -1 || value.indexOf('enquiry') !== -1) {
        return 'status-enquire';
    }
    if (
        value.indexOf('not accepting') !== -1 ||
        value.indexOf('not currently') !== -1 ||
        value.indexOf('not available') !== -1 ||
        value.indexOf('unavailable') !== -1
    ) {
        return 'status-not-accepting';
    }
    return 'status-accepting';
};

// Shared derivation used by both the card component and the map popup.
// Returns { isHost, status, meta } where meta items are { id, icon, text, label }.
// `excludeMetaIds` lets a caller drop specific host meta fields (e.g. the popup
// omits State so its narrower meta row does not wrap).
export const deriveWwoofCardData = (displayFields, { excludeMetaIds = [] } = {}) => {
    const fields = Array.isArray(displayFields) ? displayFields : [];
    const excluded = new Set(excludeMetaIds.map(String));
    const isHost = fields.some((field) => String(field.field_id) === WWOOF_STATUS_FIELD);

    if (isHost) {
        const byId = new Map(fields.map((f) => [String(f.field_id), f]));
        const status = fieldText(byId.get(WWOOF_STATUS_FIELD));
        const meta = [];
        WWOOF_HOST_META_FIELDS.forEach((cfg) => {
            if (excluded.has(String(cfg.id))) return;
            const text = cleanWwoofText(fieldText(byId.get(cfg.id)), cfg.prefixes);
            if (text) meta.push({ id: cfg.id, icon: cfg.icon, text, label: cfg.id });
        });
        return { isHost, status, meta };
    }

    const meta = [];
    fields.forEach((field) => {
        const label = decodeHtmlEntities(field.label || '').toLowerCase();
        const text = fieldText(field);
        if (!text) return;
        if (label.indexOf('age group') !== -1) {
            meta.push({ id: String(field.field_id), icon: 'user', text, label: 'age' });
        } else if (label.indexOf('gender') !== -1) {
            meta.push({
                id: String(field.field_id),
                icon: text.toLowerCase().indexOf('female') !== -1 ? 'venus' : 'mars',
                text,
                label: 'gender'
            });
        }
    });

    const seen = {};
    const deduped = meta.filter((item) => {
        const key = item.label + ':' + item.text;
        if (seen[key]) return false;
        seen[key] = true;
        return true;
    }).slice(0, 2);

    return { isHost, status: '', meta: deduped };
};

export { decodeHtmlEntities };
